/**
 * SOLOMON — LEARN PANEL
 * Day 8: Teach Solomon new content via URL, YouTube, text, or PDF.
 *
 * CONTROLLED and MOUNTED-HIDDEN (2026-07-28). Mounted once in App.jsx and
 * toggled with the `hidden` attribute — deliberately NOT routed through
 * ToolModal / activeTool like MemoryPanel, because ToolModal unmounts on
 * close and the ingestion hooks below own an in-flight transcription
 * (status / progress / result + abortRef). Unmounting on close would abandon
 * a running ingest with no way to see it again. Do not "unify" the two panels.
 *
 * Real-time SSE progress bar during ingestion.
 *
 * Each ingestion type sends to:
 *  POST /api/learn/ingest    { input }   — link or text, server-classified
 *  POST /api/learn/pdf       multipart file upload
 */

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { useAuth } from '../../context/AuthContext';
import { latexToPlainText } from '../../utils/latexNormalize';
import { pushLayer, popLayer, isTopLayer } from '../../layers';
import './learn.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────

export function BookIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Clean SSE streaming via fetch + ReadableStream.
 * Calls onEvent(eventName, data) for each SSE event.
 * Accepts an optional fetchFn (defaults to window.fetch) for auth injection.
 */
async function streamSSE(url, fetchOptions, onEvent, fetchFn = fetch) {
  const response = await fetchFn(url, fetchOptions);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }

  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let   buffer  = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Split on double-newline (SSE event separator)
    const events = buffer.split('\n\n');
    buffer = events.pop(); // last partial event

    for (const rawEvent of events) {
      if (!rawEvent.trim()) continue;

      const lines     = rawEvent.split('\n');
      let   eventName = 'message';
      let   eventData = '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventName = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          eventData = line.slice(6).trim();
        }
      }

      try {
        const parsed = JSON.parse(eventData);
        onEvent(eventName, parsed);
      } catch {
        // ignore malformed
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────
// INGESTION HOOK (clean)
// ─────────────────────────────────────────────────────────────

function useIngestion() {
  const [status,   setStatus]   = useState('idle');   // idle | loading | success | error
  const [progress, setProgress] = useState(null);     // { stage, detail, percent }
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState(null);

  // Day 9: use authFetch so all learn requests carry the JWT
  const { authFetch } = useAuth();

  // Issue 17 (LearnPanel surface): ingest is abortable — Cancel button wired
  // through ProgressBar. Mirrors the chat audio-upload AbortController flow.
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(null);
    setResult(null);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const ingest = useCallback(async (endpoint, body, isFile = false) => {
    setStatus('loading');
    setProgress({ stage: 'Connecting...', detail: '', percent: 0 });
    setResult(null);
    setError(null);

    const abortCtrl = new AbortController();
    abortRef.current = abortCtrl;

    const url = `${API_BASE}/api/learn/${endpoint}`;

    let fetchOptions;
    if (isFile) {
      fetchOptions = { method: 'POST', body, signal: abortCtrl.signal };  // FormData — authFetch adds Auth header only
    } else {
      fetchOptions = {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
        signal:  abortCtrl.signal,
      };
    }

    try {
      await streamSSE(url, fetchOptions, (eventName, data) => {
        if (eventName === 'progress') {
          setProgress(data);
        } else if (eventName === 'complete') {
          setStatus('success');
          setResult(data);
          setProgress({ stage: 'complete', detail: '', percent: 100 });
        } else if (eventName === 'error') {
          setStatus('error');
          setError(data.message || 'Something went wrong.');
        }
      }, authFetch);

      // If we got here without hitting complete/error event (edge case)
      setStatus((prev) => prev === 'loading' ? 'success' : prev);
    } catch (err) {
      if (err.name === 'AbortError') {
        // Operator cancelled — back to idle, not an error state.
        setStatus('idle');
        setProgress(null);
        return;
      }
      setStatus('error');
      setError(err.message);
    }
  }, [authFetch]);

  return { ingest, status, progress, result, error, reset, cancel };
}

// ─────────────────────────────────────────────────────────────
// SUB-PANELS
// ─────────────────────────────────────────────────────────────

function ProgressBar({ progress, status, onCancel }) {
  if (!progress && status === 'idle') return null;

  const percent = progress?.percent ?? 0;
  const stage   = progress?.stage   ?? '';
  const detail  = progress?.detail  ?? '';

  return (
    <div className="learn-progress">
      <div className="learn-progress-bar">
        <div
          className={`learn-progress-fill learn-progress-fill--${status}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="learn-progress-label">
        {status === 'success' ? '✓ Done' : stage}
        {detail ? ` — ${detail}` : ''}
        {status === 'loading' && onCancel && (
          <button
            type="button"
            className="learn-progress-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </p>
    </div>
  );
}

// Which path actually ran. With the tabs gone the operator no longer chooses,
// so this is the only cue that the server read a link the way they meant it —
// and it is precisely the cue that would have exposed Issue 89 on day one, when
// a YouTube link came back having been scraped as a web page.
const SOURCE_KIND_LABEL = {
  youtube: 'YouTube transcript',
  url:     'Article',
  text:    'Note',
  pdf:     'PDF',
  audio:   'Audio',
};

function ResultCard({ result }) {
  if (!result) return null;
  const kind = SOURCE_KIND_LABEL[result.source_type];
  return (
    <div className="learn-result">
      <div className="learn-result-title">
        <CheckIcon />
        <span>{result.source_title || 'Learned'}</span>
        {kind && <span className="learn-result-kind">{kind}</span>}
      </div>
      {result.summary && (
        <p className="learn-result-summary">{latexToPlainText(result.summary)}</p>
      )}
      {result.key_concepts?.length > 0 && (
        <div className="learn-result-concepts">
          {result.key_concepts.slice(0, 3).map((raw, i) => {
            const c = latexToPlainText(raw);
            return (
              <span key={i} className="learn-concept-chip">{c.slice(0, 60)}{c.length > 60 ? '…' : ''}</span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Ingest Tab — one input for links and text (Issue 89) ──────
//
// This replaced three tabs (URL / YouTube / Text). Choosing the path was the
// caller's job and choosing wrong was silent: a YouTube link pasted into the
// URL tab scraped the video page's shell and stored its footer as knowledge.
// The server classifies now, so there is no wrong choice to make.
//
// Deliberately NO client-side classifier mirroring the server's — a second
// copy of that rule is a second thing to drift. The helper line below states
// the behaviour; the server decides it.

function IngestTab() {
  const [input, setInput] = useState('');
  const { ingest, status, progress, result, error, reset, cancel } = useIngestion();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    ingest('ingest', { input: input.trim() });
  };

  // Enter submits only LINK-SHAPED input — one whitespace-free token. A note
  // has no newline until you press Enter for line two, so testing for one
  // would submit every note's first line and spend a Claude call on it.
  const handleKeyDown = (e) => {
    const value = input.trim();
    if (e.key === 'Enter' && !e.shiftKey && value && !/\s/.test(value)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="learn-tab-content">
      <p className="learn-tab-description">
        Paste a link or any text — an article, a video, notes, an excerpt. Solomon works out
        what it is and remembers it forever.
      </p>
      <form onSubmit={handleSubmit} className="learn-form">
        <textarea
          className="learn-textarea"
          placeholder="Paste a link or any text…"
          value={input}
          onChange={(e) => { setInput(e.target.value); if (status !== 'idle') reset(); }}
          onKeyDown={handleKeyDown}
          disabled={status === 'loading'}
          rows={4}
        />
        <button
          className="learn-submit-btn"
          type="submit"
          disabled={status === 'loading' || !input.trim()}
        >
          {status === 'loading' ? 'Learning...' : 'Learn this'}
        </button>
      </form>
      <p className="learn-tab-description">
        A YouTube link becomes a transcript · any other link is read as an article ·
        anything else is learned as a note.
      </p>
      <ProgressBar progress={progress} status={status} onCancel={cancel} />
      {status === 'success' && <ResultCard result={result} />}
      {status === 'error' && <p className="learn-error">{error}</p>}
    </div>
  );
}

// ── PDF Tab ───────────────────────────────────────────────────

function PDFTab() {
  const [file,     setFile]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef                 = useRef(null);
  const { ingest, status, progress, result, error, reset, cancel } = useIngestion();

  const handleFile = (f) => {
    if (!f || f.type !== 'application/pdf') return;
    setFile(f);
    if (status !== 'idle') reset();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    ingest('pdf', fd, true);
  };

  return (
    <div className="learn-tab-content">
      <p className="learn-tab-description">
        Upload a PDF — reports, books, whitepapers. Solomon extracts the text and stores it permanently.
      </p>
      <form onSubmit={handleSubmit} className="learn-form">
        <div
          className={`learn-drop-zone ${dragging ? 'learn-drop-zone--active' : ''} ${file ? 'learn-drop-zone--has-file' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFile(e.dataTransfer.files[0]);
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="learn-file-input"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {file ? (
            <p className="learn-drop-filename">{file.name}</p>
          ) : (
            <>
              <UploadIcon />
              <p className="learn-drop-label">Drop PDF here or click to browse</p>
            </>
          )}
        </div>
        <button
          className="learn-submit-btn"
          type="submit"
          disabled={status === 'loading' || !file}
        >
          {status === 'loading' ? 'Processing PDF...' : 'Learn this'}
        </button>
      </form>
      <ProgressBar progress={progress} status={status} onCancel={cancel} />
      {status === 'success' && <ResultCard result={result} />}
      {status === 'error' && <p className="learn-error">{error}</p>}
    </div>
  );
}

// ── Audio/Video Tab ──────────────────────────────────────────

const AUDIO_ACCEPT = '.mp3,.mp4,.wav,.m4a,.webm,.ogg,.flac,audio/*,video/mp4,video/webm';
const AUDIO_TYPES  = new Set([
  'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/webm',
  'video/mp4', 'video/webm', 'audio/ogg', 'audio/flac',
]);

function AudioTab() {
  const [file,     setFile]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef                 = useRef(null);
  const { ingest, status, progress, result, error, reset, cancel } = useIngestion();

  const handleFile = (f) => {
    if (!f) return;
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    const validExt = ['.mp3', '.mp4', '.wav', '.m4a', '.webm', '.ogg', '.flac'].includes(ext);
    if (!AUDIO_TYPES.has(f.type) && !validExt) return;
    setFile(f);
    if (status !== 'idle') reset();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    ingest('audio', fd, true);
  };

  return (
    <div className="learn-tab-content">
      <p className="learn-tab-description">
        Upload an audio or video file. Solomon will transcribe it with Whisper and extract key concepts.
      </p>
      <form onSubmit={handleSubmit} className="learn-form">
        <div
          className={`learn-drop-zone ${dragging ? 'learn-drop-zone--active' : ''} ${file ? 'learn-drop-zone--has-file' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFile(e.dataTransfer.files[0]);
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept={AUDIO_ACCEPT}
            className="learn-file-input"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {file ? (
            <p className="learn-drop-filename">{file.name}</p>
          ) : (
            <>
              <UploadIcon />
              <p className="learn-drop-label">Drop audio/video here or click to browse</p>
              <p className="learn-drop-hint">MP3, MP4, WAV, M4A, WEBM, OGG, FLAC</p>
            </>
          )}
        </div>
        <button
          className="learn-submit-btn"
          type="submit"
          disabled={status === 'loading' || !file}
        >
          {status === 'loading' ? 'Transcribing...' : 'Learn this'}
        </button>
      </form>
      <ProgressBar progress={progress} status={status} onCancel={cancel} />
      {status === 'success' && <ResultCard result={result} />}
      {status === 'error' && <p className="learn-error">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const TABS = [
  { id: 'ingest',  label: 'Link or text' },
  { id: 'pdf',     label: 'PDF'          },
  { id: 'audio',   label: 'Audio/Video'  },
];

export default function LearnPanel({ isOpen = false, onClose }) {
  const [activeTab, setTab] = useState('ingest');
  const layerId             = useId();

  // ESC closes, but only while this panel is the topmost layer — the panel is
  // permanently mounted, so the effect is gated on `isOpen` rather than on the
  // mount lifetime the way ToolModal's equivalent is.
  useEffect(() => {
    if (!isOpen) return undefined;
    pushLayer(layerId);
    const onKey = (e) => { if (e.key === 'Escape' && isTopLayer(layerId)) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => { popLayer(layerId); window.removeEventListener('keydown', onKey); };
  }, [isOpen, onClose, layerId]);

  return (
    // Rendered ALWAYS, hidden when closed — never `return null`. The tab
    // components below hold the in-flight ingest (see the file header).
    <div className="learn-panel" hidden={!isOpen}>
      <div className="learn-dropdown" role="dialog" aria-label="Teach Solomon panel">
        <div className="learn-dropdown-header">
          <BookIcon size={14} />
          <span>Teach Solomon</span>
          <button
            type="button"
            className="learn-close"
            onClick={onClose}
            aria-label="Close Teach Solomon"
          >
            ×
          </button>
        </div>

        {/* Tab bar */}
        <div className="learn-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`learn-tab ${activeTab === tab.id ? 'learn-tab--active' : ''}`}
              onClick={() => setTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="learn-tab-body">
          {activeTab === 'ingest'  && <IngestTab />}
          {activeTab === 'pdf'     && <PDFTab />}
          {activeTab === 'audio'   && <AudioTab />}
        </div>
      </div>
    </div>
  );
}
